import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { cnpj, cpf } from "cpf-cnpj-validator";
import { ActivatedRoute, Router } from '@angular/router';
import { MDBModalRef } from 'ng-uikit-pro-standard';
import { Empresa, Representantes } from 'src/app/auth/models/empresa.model';
import { IUpdateEmpresas } from 'src/app/auth/models/register.model';
import { CadastroService } from 'src/app/auth/services/cadastro.service';
import { showError, showSucess } from 'src/app/shared/utils/functions/notification.function';
import { PerfilService } from '../../services/perfil.service';
import { IUsuarioEmpresa } from '../../models/perfil.model';
import { StatusProcesso } from '../../models/processos.model';
import { emailValidator } from 'src/app/shared/utils/validators/forms.validators';

@Component({
  selector: 'app-modal-editar-representante',
  templateUrl: './modal-editar-representante.component.html',
  styleUrls: ['./modal-editar-representante.component.scss']
})
export class ModalEditarRepresentanteComponent implements OnInit {
  @Input() empresa: Empresa;
  @Input() cpfResponsavel: string;
  @Input() idPedido: string;
  @Input() editaRepresentantes: boolean;

  closeCallback: () => void;
  mdbRef: MDBModalRef;
  model: any;

  formEmpresa: FormGroup;


  default_empresa_vazia = {
    cpf: null,
    nome: null,
    emailPj: null
  }

  constructor(
    public modalRef: MDBModalRef,
    private cadastroService: CadastroService,
  ) {}

  ngOnInit(): void {
    if (!this.empresa) {
      showError("", "Nenhuma empresa selecionada para edição.");
      this.closeModal();
      return;
    }
    this.createFormEmpresa(this.empresa, false);
  }

  updateEmpresa() {
    if (this.validateFormEmpresa()) {
      const _formValue = this.formEmpresa.getRawValue();
      const _representantes = _formValue?.representantes?.filter((rep) => rep.nome && rep.cpf && rep.emailPj) || [];
      const nomeEmpresa = _formValue?.nomeEmpresa || "";

      const body: IUpdateEmpresas = {
        cnpj: this.empresa.cnpj,
        nomeEmpresa: nomeEmpresa,
        idPedido: this.idPedido,
        representantes: _representantes,
      };

      this.cadastroService.editarDadosEmpresa(body).subscribe({
        next: () => {
          this.closeModal();
          showSucess("", "Empresa atualizada com sucesso!");
        },
        error: (e) => {
          showError("", e.error?.message || "Ocorreu um erro, tente novamente.");
        }
      });
    }
  }

  get representantes(): FormArray {
    return this.formEmpresa.get('representantes') as FormArray;
  }

  private validateFormEmpresa(): boolean {
    const emp = this.formEmpresa.value;
    if (!emp.nomeEmpresa || emp.nomeEmpresa.trim().length === 0) {
      showError("", "O campo Razão Social é obrigatório.");
      return false;
    }

    if (this.formEmpresa.get("nomeEmpresa")?.hasError("pattern")) {
      showError("", "O campo Razão Social contém caracteres inválidos.");
      return false;
    }

    const cpfs: string[] = [];
    const nomes: string[] = [];
    const emails: string[] = [];

    const representantes = this.formEmpresa.get("representantes") as FormArray;
    const emailRegex = /^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    for (let i = 0; i < representantes.length; i++) {
      const rep = representantes.at(i).value;

      if(i > 0 && (!rep.nome && !rep.cpf && !rep.emailPj)) {
        continue; // Representante opcional não preenchido
      }

      if (!rep.nome || !rep.cpf || !rep.emailPj) {
        if(i === 0) {
          showError("", `Todos os campos do representante ${i + 1} são obrigatórios.`);
        } else{
          showError("", `Preencha todos os campos do representante ${i + 1}, ou deixe todos em branco.`);
        }
        return false;
      }
      if (!emailRegex.test(rep.emailPj)) {
        showError("", `E-mail do representante ${i + 1} é inválido.`);
        return false;
      }
      if (!cpf.isValid(rep.cpf)) {
        showError("", `CPF do representante ${i + 1} é inválido.`);
        return false;
      }
      if (cpfs.includes(rep.cpf)) {
        showError("", `CPF do representante ${i + 1} está duplicado.`);
        return false;
      }
      if (nomes.includes(rep.nome)) {
        showError("", `Nome do representante ${i + 1} está duplicado.`);
        return false;
      }
      if (emails.includes(rep.emailPj)) {
        showError("", `Email do representante ${i + 1} está duplicado.`);
        return false;
      }

      cpfs.push(rep.cpf);
      nomes.push(rep.nome);
      emails.push(rep.emailPj);
    }

    return true;
  }

  private createFormEmpresa(value: Empresa, disabled: boolean) {
    const arrayRepresentantes = new FormArray([]);

    for (let index = 0; index < 2; index++) {
      const _representante = value?.representantes[index] || this.default_empresa_vazia;
      arrayRepresentantes.push(this.createFormRepresentante(_representante, index === 0));
    }

    this.formEmpresa = new FormGroup({
      cnpj: new FormControl({ value: value.cnpj, disabled: true }, [Validators.required]),
      nomeEmpresa: new FormControl({value: value.nomeEmpresa || "", disabled: !this.editaRepresentantes}, [Validators.required, Validators.pattern(/^[^!"?\[\\\]‘’“”«»–.]*$/)]),
      representantes: arrayRepresentantes
    });
  }

  private createFormRepresentante(rep?: Representantes, required = true) {
    const validarNome = required ? [Validators.required, Validators.minLength(3)] : [Validators.minLength(3)];
    const validarCpf = required ? [Validators.required, Validators.minLength(11), Validators.maxLength(14)] : [Validators.minLength(11), Validators.maxLength(14)];
    const validarEmail = required ? [Validators.required, Validators.maxLength(50), emailValidator] : [Validators.maxLength(50), emailValidator];

    return new FormGroup({
      nome: new FormControl({value: rep?.nome || '', disabled: !this.editaRepresentantes}, validarNome),
      cpf: new FormControl({value: rep?.cpf || '', disabled: !this.editaRepresentantes}, validarCpf),
      emailPj: new FormControl({value: rep?.emailPj || '', disabled: !this.editaRepresentantes}, validarEmail)
    });
  }


  closeModal() {
    if (this.closeCallback) {
      this.closeCallback();
    }

    return this.modalRef.hide();
  }
}
